import React from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BombiixCard } from '../../Components/Card/BombiixCard';
import { NewCheckbox } from '../../Components/PIM/common/NewCheckbox';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import { shortenStringWithTooltip, toShowArrayToString } from '../../Components/helper';
import { DeleteIcon, ArchiveIcon, PinIcon, InfoIcon, ChatIcon } from '../../bombiix-icon';
import { PlaceholderCardItem } from './types';
import './placeholder.css';

export interface PlaceholderCardTileProps {
  item: PlaceholderCardItem;
  index: number;
  selectedIds: string[];
  isArchived: boolean;
  isSupplierLoggedIn: boolean;
  onCheck: (_event: React.ChangeEvent<HTMLInputElement>, _id?: string) => void;
  handlePin: (_item: PlaceholderCardItem) => void;
  deleteCard: (_id: string | null) => void;
  handleExpand: (_item: PlaceholderCardItem) => void;
  PlaceholderAddEditAccess: boolean;
  PlaceholderDeleteAccess: boolean;
  subBrandAccess: boolean;
  archiveCard: (_id: string | null) => void;
}

/**
 * Single placeholder card tile rendered inside the placeholder-management grid.
 * Uses the shared BombiixCard master component (Bombiix 2026 card design) and
 * the master-design icon-container buttons for the header actions.
 */
function PlaceholderCardTile({
  item,
  index,
  selectedIds,
  isArchived,
  isSupplierLoggedIn,
  onCheck,
  handlePin,
  deleteCard,
  handleExpand,
  PlaceholderAddEditAccess,
  PlaceholderDeleteAccess,
  subBrandAccess,
  archiveCard,
}: PlaceholderCardTileProps): React.ReactElement {
  const { t } = useTranslation('common');

  const dateFormat = 'DD-MMMM-YYYY, h:mm a';

  /** Info popover listing created-by / modified-by metadata for the card. */
  const showCreatedandModified = (): React.ReactElement => {
    return (
      <OverlayTrigger
        placement="top"
        trigger="hover"
        rootClose
        overlay={
          <Popover id={`placeholder-modified-popover-${index}`} className="custom-popover modified-popover">
            <Popover.Content>
              <div>
                {item?.createdBy?.firstName && item?.createdBy?.lastName && (
                  <p className="text-left m-0">
                    <strong>{t('PRODUCT_CP_LISTING.CREATED')}:</strong> {`${item?.createdBy?.firstName} ${item?.createdBy?.lastName} | `}
                    {moment(item?.createdAt).format(dateFormat)}
                  </p>
                )}
                {item?.modifiedBy?.firstName && item?.modifiedBy?.lastName && (
                  <p className="text-left m-0">
                    <strong>{t('PRODUCT_CP_LISTING.MODIFIED')}:</strong> {`${item?.modifiedBy?.firstName} ${item?.modifiedBy?.lastName} | `}
                    {moment(item?.updatedAt).format(dateFormat)}
                  </p>
                )}
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <button type="button" className="bombiix-icon-container" aria-label="info">
          <InfoIcon size={24} />
        </button>
      </OverlayTrigger>
    );
  };

  /** Archive metadata popover shown on archived cards. */
  const showComment = (): React.ReactElement => {
    return (
      <div className="custom-popover-container testing-action-spaceremoval">
        <button type="button" className="bombiix-icon-container" aria-label="archive-info">
          <ChatIcon size={24} />
        </button>
        <div className="custom-popover-content">
          <span className="arrow" />
          {item?.archivedBy?.firstName && (
            <p className="text-left m-0">
              {`${item?.archivedBy?.firstName} ${item.archivedBy?.lastName} | `}
              {item?.archivedAt ? moment(item?.archivedAt).format(dateFormat) : null}
            </p>
          )}
          {item?.archiveComment && !isEmpty(item?.archiveComment) && (
            <div className="comment">
              <p className="m-0">{item?.archiveComment}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleTitleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    handleExpand(item);
  };

  return (
    <BombiixCard>
      {!isSupplierLoggedIn && (
        <BombiixCard.Header>
          <BombiixCard.Actions>
            {(PlaceholderDeleteAccess || PlaceholderAddEditAccess) && (
              <li>
                <NewCheckbox
                  checked={selectedIds?.includes(item?._id)}
                  disabled={(!PlaceholderAddEditAccess && !PlaceholderDeleteAccess) || item?.isDefaultCard}
                  onChange={(e) => {
                    onCheck(e, item?._id);
                  }}
                />
              </li>
            )}

            <li>{showCreatedandModified()}</li>

            {PlaceholderAddEditAccess && !item?.isDefaultCard && (
              <li>
                <CustomTooltip tooltipText={item?.isArchived ? t('PRODUCT_CP_LISTING.UNARCHIVE') : t('PRODUCT_CP_LISTING.ARCHIVE')}>
                  <button
                    type="button"
                    onClick={() => archiveCard(item?._id)}
                    aria-label={item?.isArchived ? t('PRODUCT_CP_LISTING.UNARCHIVE') : t('PRODUCT_CP_LISTING.ARCHIVE')}
                    className="bombiix-icon-container"
                  >
                    <ArchiveIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}

            {item?.isArchived && <li>{showComment()}</li>}

            {PlaceholderDeleteAccess && isArchived && (
              <li>
                <CustomTooltip tooltipText={t('PRODUCT_CP_LISTING.DELETE')}>
                  <button type="button" aria-label={t('PRODUCT_CP_LISTING.DELETE')} className="bombiix-icon-container" onClick={() => deleteCard(item?._id)}>
                    <DeleteIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
          </BombiixCard.Actions>

          <BombiixCard.Actions>
            {!item?.isArchived && PlaceholderAddEditAccess && (
              <li>
                <CustomTooltip tooltipText={t('PRODUCT_CP_LISTING.PIN')}>
                  <button
                    type="button"
                    onClick={() => handlePin(item)}
                    className={`bombiix-icon-container ${item?.pinned ? 'bombiix-icon-container--green' : ''}`}
                    aria-label={t('PRODUCT_CP_LISTING.PIN')}
                    disabled={!PlaceholderAddEditAccess}
                  >
                    <PinIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
          </BombiixCard.Actions>
        </BombiixCard.Header>
      )}

      <BombiixCard.Body>
        <BombiixCard.Title onClick={handleTitleClick}>{shortenStringWithTooltip(item?.name, 30)}</BombiixCard.Title>
        <BombiixCard.Meta onClick={() => handleExpand(item)}>
          <li>
            <strong>{t('PRODUCT_CP_LISTING.BRAND')}: </strong>
            {toShowArrayToString(item, 'brand', 30)}
          </li>
          {subBrandAccess && (
            <li>
              <strong>{t('PRODUCT_CP_LISTING.SUBBRAND')}: </strong>
              {toShowArrayToString(item, 'subBrand', 30)}
            </li>
          )}
          <li>
            <strong>{t('PRODUCT_CP_LISTING.PRODUCT_TYPE')}: </strong>
            {toShowArrayToString(item, 'productType', 30)}
          </li>
          <li>
            <strong>{t('PRODUCT_CP_LISTING.CATEGORY_ONE')}: </strong>
            {toShowArrayToString(item, 'catOneId', 30)}
          </li>
          <li>
            <strong>{t('PRODUCT_CP_LISTING.CATEGORY_TWO')}: </strong>
            {toShowArrayToString(item, 'catTwoId', 30)}
          </li>
          <li>
            <strong>{t('PRODUCT_CP_LISTING.SEASON')}: </strong>
            {toShowArrayToString(item, 'season', 30)}
          </li>
          <li>
            <strong>{t('PRODUCT_CP_LISTING.YEAR')}: </strong>
            {item?.year?.map((re) => `${re}, `).join(' ')}
          </li>
          <li>
            <strong>{t('PRODUCT_CP_LISTING.MONTH')}: </strong>
            {item?.month?.map((re) => `${re}, `).join(' ')}
          </li>
        </BombiixCard.Meta>
      </BombiixCard.Body>
    </BombiixCard>
  );
}

export default PlaceholderCardTile;
